import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { hourBankName } from "../lib/hourbank";
import { toast } from "sonner";

// RC21 — ações administrativas de cliente compartilhadas entre
// Gestão → Clientes e Ficha 360° (implementação única).

const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : (d?.message || fallback);
};

const Overlay = ({ children, testid }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid={testid}>
    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">{children}</div>
  </div>
);

const FIELDS = [
  ["name", "Nome"], ["email", "E-mail"], ["phone", "Telefone"],
  ["cpf", "CPF"], ["rg", "RG"], ["company_name", "Empresa"],
];

export function EditClientModal({ client, onClose, onSaved }) {
  const [form, setForm] = useState(() =>
    Object.fromEntries(FIELDS.map(([k]) => [k, client[k] || ""])));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const [k] of FIELDS) {
        if ((form[k] || "") !== (client[k] || "")) payload[k] = form[k];
      }
      if (Object.keys(payload).length === 0) { onClose(); return; }
      const { data } = await api.put(`/clients/${client.id}`, payload);
      toast.success("Cliente atualizado");
      onSaved(data);
    } catch (e) {
      toast.error(errMsg(e, "Erro ao salvar cliente"));
    } finally { setSaving(false); }
  };

  return (
    <Overlay testid="edit-client-modal">
      <h3 className="font-semibold text-[#14213D] text-lg mb-4">Editar cliente</h3>
      <div className="space-y-3">
        {FIELDS.map(([k, label]) => (
          <div key={k}>
            <label className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">{label}</label>
            <input
              value={form[k]}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              className="mt-1 w-full border border-gray-200 rounded px-3 py-2 text-sm"
              data-testid={`edit-client-${k}`}
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} data-testid="edit-client-cancel"
          className="px-4 py-2 text-sm rounded border border-gray-200 text-gray-600 hover:bg-gray-50">Cancelar</button>
        <button onClick={save} disabled={saving} data-testid="edit-client-save"
          className="px-4 py-2 text-sm rounded bg-[#14213D] text-white font-semibold hover:bg-[#1F335E] disabled:opacity-50">
          {saving ? "Salvando…" : "Salvar"}
        </button>
      </div>
    </Overlay>
  );
}

export function DeleteClientModal({ client, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const del = async () => {
    setBusy(true);
    try {
      await api.delete(`/clients/${client.id}`);
      toast.success("Cliente excluído da operação");
      onDeleted();
    } catch (e) {
      toast.error(errMsg(e, "Erro ao excluir cliente"));
    } finally { setBusy(false); }
  };
  return (
    <Overlay testid="delete-client-modal">
      <h3 className="font-semibold text-[#14213D] text-lg mb-2">Excluir cliente?</h3>
      <p className="text-sm text-gray-600 mb-2">
        <b>{client.name}</b> será removido das listas operacionais (exclusão lógica).
      </p>
      <p className="text-xs text-gray-500 mb-5">
        Reservas, pagamentos, Banco de Horas e auditoria serão preservados para histórico.
      </p>
      <div className="flex justify-end gap-2">
        <button onClick={onClose} data-testid="delete-client-cancel"
          className="px-4 py-2 text-sm rounded border border-gray-200 text-gray-600 hover:bg-gray-50">Cancelar</button>
        <button onClick={del} disabled={busy} data-testid="delete-client-confirm"
          className="px-4 py-2 text-sm rounded bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-50">
          {busy ? "Excluindo…" : "Confirmar exclusão"}
        </button>
      </div>
    </Overlay>
  );
}

export function AdjustHoursModal({ clientId, onClose, onDone }) {
  const [pkgs, setPkgs] = useState(null);
  const [pkgId, setPkgId] = useState("");
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/clients/${clientId}/hour-bank`).then(({ data }) => {
      const list = data.packages || [];
      setPkgs(list);
      if (list.length > 0) setPkgId(list[0].id);
    }).catch(() => setPkgs([]));
  }, [clientId]);

  const selected = (pkgs || []).find((p) => p.id === pkgId);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/hour-bank/adjust`, {
        package_id: pkgId, hours_delta: Number(delta), reason,
      });
      toast.success(
        `Ajuste registrado: ${data.balance_before}h → ${data.hours_adjusted > 0 ? "+" : ""}${data.hours_adjusted}h → ${data.balance_after}h`
      );
      onDone();
    } catch (e) {
      toast.error(errMsg(e, "Erro ao ajustar horas"));
    } finally { setBusy(false); }
  };

  return (
    <Overlay testid="adjust-hours-modal">
      <h3 className="font-semibold text-[#14213D] text-lg mb-1">Ajustar horas disponíveis</h3>
      <p className="text-xs text-gray-500 mb-4">O ajuste é registrado na auditoria e no extrato do Banco de Horas.</p>
      {pkgs === null ? (
        <div className="text-sm text-gray-400 py-6 text-center">Carregando…</div>
      ) : pkgs.length === 0 ? (
        <div className="text-sm text-gray-400 py-6 text-center" data-testid="adjust-no-packages">Este cliente não possui Banco de Horas.</div>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Banco de Horas</label>
            <select value={pkgId} onChange={(e) => setPkgId(e.target.value)}
              className="mt-1 w-full border border-gray-200 rounded px-3 py-2 text-sm" data-testid="adjust-package-select">
              {pkgs.map((p) => (
                <option key={p.id} value={p.id}>
                  {hourBankName(p)} — saldo {p.hours_remaining}h ({p.status})
                </option>
              ))}
            </select>
          </div>
          {selected && (
            <div className="bg-gray-50 rounded px-3 py-2 text-xs text-gray-600" data-testid="adjust-current-balance">
              Saldo anterior: <b className="text-[#14213D]">{selected.hours_remaining}h</b> · {selected.hours_total}h adquiridas · {selected.hours_used}h utilizadas
            </div>
          )}
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Horas a ajustar (+ adiciona / − remove)</label>
            <input type="number" step="0.5" value={delta} onChange={(e) => setDelta(e.target.value)}
              placeholder="Ex.: 2 ou -1.5"
              className="mt-1 w-full border border-gray-200 rounded px-3 py-2 text-sm" data-testid="adjust-hours-input" />
            {selected && delta && !isNaN(Number(delta)) && (
              <div className="text-xs mt-1 text-gray-500">
                Saldo posterior: <b className="text-emerald-700">{Math.round((selected.hours_remaining + Number(delta)) * 100) / 100}h</b>
              </div>
            )}
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Motivo do ajuste (obrigatório)</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              placeholder="Ex.: correção de check-out registrado errado"
              className="mt-1 w-full border border-gray-200 rounded px-3 py-2 text-sm" data-testid="adjust-reason-input" />
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} data-testid="adjust-cancel"
          className="px-4 py-2 text-sm rounded border border-gray-200 text-gray-600 hover:bg-gray-50">Cancelar</button>
        <button onClick={submit} disabled={busy || !pkgId || !delta || reason.trim().length < 3}
          data-testid="adjust-confirm"
          className="px-4 py-2 text-sm rounded bg-[#14213D] text-white font-semibold hover:bg-[#1F335E] disabled:opacity-50">
          {busy ? "Registrando…" : "Registrar ajuste"}
        </button>
      </div>
    </Overlay>
  );
}
